import { SampleSentence } from 'src/api/learningSessions/types';
import {
  ExampleSentenceSortDirection,
  ExampleSentenceSortField,
} from 'src/utils/exampleSentenceSortPreference';

export type HighlightRange = {
  start: number;
  end: number;
};

type FoldedText = {
  folded: string;
  /** Maps each folded character index to its original string index. */
  indexMap: number[];
};

type WordToken = {
  text: string;
  start: number;
  end: number;
  folded: string;
};

// Use RegExp() instead of literals: Next/SWC can throw `charCodeAt is not a function`
// when transforming unicode property escapes like `\p{L}` in `/.../u` literals.
const WORD_TOKEN_PATTERN = new RegExp('\\p{L}[\\p{L}\\p{M}]*', 'gu');
const SURROUNDING_PUNCTUATION = new RegExp('^[^\\p{L}\\p{N}]+|[^\\p{L}\\p{N}]+$', 'gu');
const COMBINING_MARKS = new RegExp('\\p{M}', 'gu');
const LETTER_PATTERN = new RegExp('\\p{L}', 'u');
const IDEOGRAPHIC_SCRIPT_PATTERN = new RegExp(
  '\\p{Script=Han}|\\p{Script=Hiragana}|\\p{Script=Katakana}|\\p{Script=Hangul}',
  'u',
);

export const formatSampleSentenceCreator = (
  source: string | null | undefined,
  author: string | null | undefined,
): string | null => {
  const trimmedSource = source?.trim();
  if (!trimmedSource) {
    return null;
  }

  const trimmedAuthor = author?.trim();
  if (trimmedAuthor) {
    return `${trimmedSource} (${trimmedAuthor})`;
  }

  return trimmedSource;
};

const foldChar = (char: string): string =>
  char.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();

const buildFoldedText = (text: string): FoldedText => {
  let folded = '';
  const indexMap: number[] = [];

  for (let i = 0; i < text.length; i += 1) {
    const foldedChar = foldChar(text[i]);
    for (let j = 0; j < foldedChar.length; j += 1) {
      folded += foldedChar[j];
      indexMap.push(i);
    }
  }

  return { folded, indexMap };
};

const normalizeLearnable = (learnable: string): string =>
  learnable.trim().replace(SURROUNDING_PUNCTUATION, '');

const rangeFromFoldedMatch = (
  indexMap: number[],
  foldedStart: number,
  foldedLength: number,
): HighlightRange => {
  const start = indexMap[foldedStart];
  const end = indexMap[foldedStart + foldedLength - 1] + 1;
  return { start, end };
};

const hasIdeographicScript = (value: string): boolean => IDEOGRAPHIC_SCRIPT_PATTERN.test(value);

const isLetter = (value: string | undefined): boolean => !!value && LETTER_PATTERN.test(value);

const findSubstringFoldedRange = (sentence: string, learnable: string): HighlightRange | null => {
  const normalizedLearnable = normalizeLearnable(learnable);
  if (!normalizedLearnable) {
    return null;
  }

  const { folded: foldedSentence, indexMap } = buildFoldedText(sentence);
  const { folded: foldedLearnable } = buildFoldedText(normalizedLearnable);
  if (!foldedLearnable) {
    return null;
  }

  const foldedStart = foldedSentence.indexOf(foldedLearnable);
  if (foldedStart === -1) {
    return null;
  }

  const range = rangeFromFoldedMatch(indexMap, foldedStart, foldedLearnable.length);
  const charBefore = range.start > 0 ? sentence[range.start - 1] : undefined;
  const charAfter = range.end < sentence.length ? sentence[range.end] : undefined;
  const isMidWord = isLetter(charBefore) || isLetter(charAfter);

  // Allow mid-run matches for CJK scripts; reject Latin substrings inside larger words (e.g. "a" in "am").
  if (isMidWord && !hasIdeographicScript(normalizedLearnable)) {
    return null;
  }

  return range;
};

const tokenizeWords = (text: string): WordToken[] => {
  const tokens: WordToken[] = [];
  const matcher = new RegExp(WORD_TOKEN_PATTERN.source, WORD_TOKEN_PATTERN.flags);
  let match = matcher.exec(text);

  while (match) {
    tokens.push({
      text: match[0],
      start: match.index,
      end: match.index + match[0].length,
      folded: buildFoldedText(match[0]).folded,
    });
    match = matcher.exec(text);
  }

  return tokens;
};

const findWordAlignedExactRange = (sentence: string, learnable: string): HighlightRange | null => {
  const normalizedLearnable = normalizeLearnable(learnable);
  if (!normalizedLearnable) {
    return null;
  }

  const learnableWords = tokenizeWords(normalizedLearnable).map(token => token.folded);
  const sentenceWords = tokenizeWords(sentence);

  if (learnableWords.length === 0 || sentenceWords.length < learnableWords.length) {
    return null;
  }

  for (let start = 0; start <= sentenceWords.length - learnableWords.length; start += 1) {
    const window = sentenceWords.slice(start, start + learnableWords.length);
    const isExact = window.every((token, index) => token.folded === learnableWords[index]);
    if (isExact) {
      return {
        start: window[0].start,
        end: window[window.length - 1].end,
      };
    }
  }

  return null;
};

const sharedPrefixLength = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) {
    i += 1;
  }
  return i;
};

const areRelatedForms = (learnableWord: string, sentenceWord: string): boolean => {
  if (!learnableWord || !sentenceWord) {
    return false;
  }

  if (learnableWord === sentenceWord) {
    return true;
  }

  // Avoid highlighting unrelated short words (e.g. single letters / particles).
  const shorter = Math.min(learnableWord.length, sentenceWord.length);
  if (shorter < 2) {
    return false;
  }

  const shared = sharedPrefixLength(learnableWord, sentenceWord);
  const requiredShared = Math.max(2, Math.ceil(shorter * 0.6));
  return shared >= requiredShared;
};

const scoreRelatedWindow = (learnableWords: string[], sentenceWords: WordToken[]): number => {
  let score = 0;

  for (let i = 0; i < learnableWords.length; i += 1) {
    if (!areRelatedForms(learnableWords[i], sentenceWords[i].folded)) {
      return -1;
    }
    score += sharedPrefixLength(learnableWords[i], sentenceWords[i].folded);
  }

  return score;
};

const findRelatedFormRange = (sentence: string, learnable: string): HighlightRange | null => {
  const normalizedLearnable = normalizeLearnable(learnable);
  if (!normalizedLearnable) {
    return null;
  }

  const learnableWords = tokenizeWords(normalizedLearnable).map(token => token.folded);
  const sentenceWords = tokenizeWords(sentence);

  if (learnableWords.length === 0 || sentenceWords.length < learnableWords.length) {
    return null;
  }

  let bestScore = -1;
  let bestRange: HighlightRange | null = null;

  for (let start = 0; start <= sentenceWords.length - learnableWords.length; start += 1) {
    const window = sentenceWords.slice(start, start + learnableWords.length);
    const score = scoreRelatedWindow(learnableWords, window);
    if (score > bestScore) {
      bestScore = score;
      bestRange = {
        start: window[0].start,
        end: window[window.length - 1].end,
      };
    }
  }

  return bestRange;
};

/**
 * Finds where the learnable (or a closely related form) appears in a sample sentence.
 * Prefers a word-aligned exact/diacritic-insensitive match, then related word forms
 * that share a substantial stem/prefix (e.g. großartig → großartige), then a raw
 * substring match for scripts without clear word separators.
 */
export const findLearnableHighlightRange = (
  sentence: string,
  learnable: string | null | undefined,
): HighlightRange | null => {
  if (!learnable?.trim() || !sentence) {
    return null;
  }

  return (
    findWordAlignedExactRange(sentence, learnable) ??
    findRelatedFormRange(sentence, learnable) ??
    findSubstringFoldedRange(sentence, learnable)
  );
};

/**
 * Drops sentences CAS has flagged (severe/offensive content, or a duplicate of
 * the learnable / of another sentence). NSFW sentences are deliberately kept:
 * they're casual profanity at most, and are still delivered to learners.
 */
export const filterOutFlaggedSentences = (sampleSentences: SampleSentence[]): SampleSentence[] =>
  sampleSentences.filter(sentence => !sentence.isFlagged);

/** Sample sentence difficulty scores (and the derived user-score target) live on a 0-10 scale. */
export const SENTENCE_DIFFICULTY_MAX = 10;

// Matches VISIBLE_EXAMPLES_COUNT in ./index.tsx: below this we don't have enough
// scored sentences to make reordering meaningful, so we leave the backend order alone.
const MIN_SCORED_SENTENCES_FOR_RANKING = 2;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/** Maps the learner's 0-1 language-pair score onto the sentence difficulty scale. */
export const toTargetSentenceDifficulty = (userScore: number | null | undefined): number | null => {
  if (userScore === null || userScore === undefined || !Number.isFinite(userScore)) {
    return null;
  }

  return clamp(userScore, 0, 1) * SENTENCE_DIFFICULTY_MAX;
};

/** Formats a CAS difficulty score as a "x/10" label, rounded to 1 decimal place. */
export const formatSentenceDifficultyScore = (difficultyScore: number): string => {
  const rounded = Math.round(clamp(difficultyScore, 0, SENTENCE_DIFFICULTY_MAX) * 10) / 10;
  return `${rounded}/${SENTENCE_DIFFICULTY_MAX}`;
};

export type SentenceDifficultyTone = 'easy' | 'mid' | 'hard' | 'expert';

/** Mirrors CAS's difficultyTone() bands (DifficultyScorePill.jsx) so pills read the same way in both tools. */
export const getSentenceDifficultyTone = (difficultyScore: number): SentenceDifficultyTone => {
  if (difficultyScore < 4) {
    return 'easy';
  }
  if (difficultyScore < 6) {
    return 'mid';
  }
  if (difficultyScore < 8) {
    return 'hard';
  }
  return 'expert';
};

type DecoratedSentence = {
  sentence: SampleSentence;
  difficulty: number | null;
  index: number;
};

const decorateWithDifficulty = (sampleSentences: SampleSentence[]): DecoratedSentence[] =>
  sampleSentences.map((sentence, index) => {
    const { difficultyScore } = sentence;
    const difficulty =
      difficultyScore !== null && Number.isFinite(difficultyScore)
        ? clamp(difficultyScore, 0, SENTENCE_DIFFICULTY_MAX)
        : null;
    return { sentence, difficulty, index };
  });

/**
 * Orders sample sentences by relevance to the learner's target difficulty: nearest first.
 * For a beginner (target near 0) this is equivalent to a strict easy-to-hardest ladder;
 * for an intermediate/advanced learner it surfaces near-level sentences first regardless
 * of direction. Sentences without a usable difficulty score are pushed to the end, keeping
 * their relative backend order, so the ranking degrades gracefully while scoring backfills.
 */
export const orderSampleSentencesByRelevance = (
  sampleSentences: SampleSentence[],
  targetDifficulty: number | null,
): SampleSentence[] => {
  if (targetDifficulty === null) {
    return sampleSentences;
  }

  const decorated = decorateWithDifficulty(sampleSentences);
  const scored = decorated.filter(item => item.difficulty !== null);

  if (scored.length < MIN_SCORED_SENTENCES_FOR_RANKING) {
    return sampleSentences;
  }

  const unscored = decorated.filter(item => item.difficulty === null);

  const sortedScored = [...scored].sort((a, b) => {
    const difficultyA = a.difficulty as number;
    const difficultyB = b.difficulty as number;
    const distanceA = Math.abs(difficultyA - targetDifficulty);
    const distanceB = Math.abs(difficultyB - targetDifficulty);

    if (distanceA !== distanceB) {
      return distanceA - distanceB;
    }
    if (difficultyA !== difficultyB) {
      return difficultyA - difficultyB;
    }
    return a.index - b.index;
  });

  return [...sortedScored, ...unscored].map(item => item.sentence);
};

const finiteOrNull = (value: number | null): number | null =>
  value !== null && Number.isFinite(value) ? value : null;

const compareNullableNumber = (
  a: number | null,
  b: number | null,
  direction: ExampleSentenceSortDirection,
): number => {
  if (a === null && b === null) {
    return 0;
  }
  if (a === null) {
    return 1;
  }
  if (b === null) {
    return -1;
  }
  return direction === 'asc' ? a - b : b - a;
};

const compareSource = (
  a: string | null | undefined,
  b: string | null | undefined,
  direction: ExampleSentenceSortDirection,
): number => {
  const sourceA = a?.trim() ?? '';
  const sourceB = b?.trim() ?? '';

  if (!sourceA && !sourceB) {
    return 0;
  }
  if (!sourceA) {
    return 1;
  }
  if (!sourceB) {
    return -1;
  }

  const result = sourceA.localeCompare(sourceB);
  return direction === 'asc' ? result : -result;
};

/**
 * Sorts sample sentences by an explicit overflow-menu criterion.
 * Missing difficulty scores and blank sources always sort last so the ranking
 * stays usable while those fields backfill. Ties keep the incoming order.
 */
export const orderSampleSentencesBySort = (
  sampleSentences: SampleSentence[],
  field: ExampleSentenceSortField,
  direction: ExampleSentenceSortDirection,
): SampleSentence[] =>
  sampleSentences
    .map((sentence, index) => ({ sentence, index }))
    .sort((a, b) => {
      const result =
        field === 'difficulty'
          ? compareNullableNumber(
              finiteOrNull(a.sentence.difficultyScore),
              finiteOrNull(b.sentence.difficultyScore),
              direction,
            )
          : field === 'length'
            ? compareNullableNumber(
                a.sentence.targetValue.length,
                b.sentence.targetValue.length,
                direction,
              )
            : compareSource(a.sentence.source, b.sentence.source, direction);

      if (result !== 0) {
        return result;
      }
      return a.index - b.index;
    })
    .map(item => item.sentence);
